import {
  ArchiveIcon as ArchiveIconOutline,
  ClockIcon as ClockIconOutline,
  CloudIcon as CloudIconOutline,
  CreditCardIcon as CreditCardIconOutline,
  CubeIcon as CubeIconOutline,
  CurrencyDollarIcon as CurrencyDollarIconOutline,
  KeyIcon as KeyIconOutline,
  OfficeBuildingIcon as OfficeBuildingIconOutline,
  SwitchVerticalIcon as SwitchVerticalIconOutline,
  UserIcon as UserIconOutline,
  VariableIcon as VariableIconOutline,
} from "@heroicons/react/outline";
import {
  ArchiveIcon,
  ClockIcon,
  CloudIcon,
  CreditCardIcon,
  CubeIcon,
  CurrencyDollarIcon,
  KeyIcon,
  OfficeBuildingIcon,
  SwitchVerticalIcon,
  UserIcon,
  VariableIcon,
} from "@heroicons/react/solid";

import { ProviderWithLogo } from "@/components/ProviderWithLogo";
import { Mcp, Slack as SlackIcon } from "@/components/ui/icons";

export const SETTINGS_TABS = [
  {
    name: "Account Settings",
    panelTitle: "Account Settings",
    group: "Account",
    path: "user",
    panelDescription: "Manage your account settings.",
    outlineIcon: <UserIconOutline className="h-5 w-auto" />,
    solidIcon: <UserIcon className="h-5 w-auto" />,
  },
  {
    name: "Organizations",
    panelTitle: "Organizations",
    group: "Account",
    path: "organizations",
    panelDescription: "View and manage all your organizations and members.",
    outlineIcon: <OfficeBuildingIconOutline className="h-5 w-auto" />,
    solidIcon: <OfficeBuildingIcon className="h-5 w-auto" />,
  },
  {
    name: "Billing",
    panelTitle: "Billing & Usage",
    group: "Organization",
    docsLink: "https://docs.promptlayer.com/why-promptlayer/how-it-works#pricing",
    panelDescription: "View and manage your billing and usage.",
    outlineIcon: <CreditCardIconOutline className="h-5 w-auto" />,
    solidIcon: <CreditCardIcon className="h-5 w-auto" />,
    path: `billing`,
  },
  {
    name: "PromptLayer API Keys",
    panelTitle: "Your PromptLayer API Keys",
    group: "Workspace",
    panelDescription: "Manage your PromptLayer API keys.",
    path: "promptlayer-api-keys",
    outlineIcon: <KeyIconOutline className="h-5 w-auto" />,
    solidIcon: <KeyIcon className="h-5 w-auto" />,
  },
  {
    name: "Custom Providers and Models",
    panelTitle: "Your Custom Providers and Models",
    group: "Workspace",
    panelDescription: "Manage your custom providers and models.",
    path: "custom-providers-and-models",
    outlineIcon: <CubeIconOutline className="h-5 w-auto" />,
    solidIcon: <CubeIcon className="h-5 w-auto" />,
  },
  {
    name: "Custom Model Pricing",
    panelTitle: "Custom Model Pricing",
    group: "Workspace",
    panelDescription:
      "Set prices for models PromptLayer doesn't support, so your logs show cost instead of $0.",
    path: "custom-model-pricing",
    outlineIcon: <CurrencyDollarIconOutline className="h-5 w-auto" />,
    solidIcon: <CurrencyDollarIcon className="h-5 w-auto" />,
  },
  {
    name: "Model API Keys",
    panelTitle: "Your Saved API Keys",
    group: "Workspace",
    panelDescription: "Manage your saved API keys.",
    path: "saved-api-keys",
    outlineIcon: <CloudIconOutline className="h-5 w-auto" />,
    solidIcon: <CloudIcon className="h-5 w-auto" />,
    subTabs: [
      {
        name: "OpenAI",
        id: 0,
        panelTitle: "OpenAI API Key",
        panelDescription: "Manage your OpenAI API keys.",
        path: `openai`,
        outlineIcon: <ProviderWithLogo hideName provider="openai" />,
        solidIcon: <ProviderWithLogo hideName filter="filter-blue-400" provider="openai" />,
      },
      {
        name: "Anthropic",
        id: 1,
        panelTitle: "Anthropic API Key",
        panelDescription: "Manage your Anthropic API keys.",
        path: "anthropic",
        outlineIcon: <ProviderWithLogo hideName provider="anthropic" />,
        solidIcon: <ProviderWithLogo hideName provider="anthropic" filter="filter-blue-400" />,
      },
      {
        name: "Google",
        id: 2,
        panelTitle: "Google API Key",
        panelDescription: "Manage your Google API keys.",
        path: "google",
        outlineIcon: <ProviderWithLogo hideName provider="google" />,
        solidIcon: <ProviderWithLogo hideName provider="google" filter="filter-blue-400" />,
      },
      {
        name: "Cohere",
        id: 3,
        panelTitle: "Cohere API Key",
        panelDescription: "Manage your Cohere API keys.",
        path: "cohere",
        outlineIcon: <ProviderWithLogo hideName provider="cohere" />,
        solidIcon: <ProviderWithLogo hideName provider="cohere" filter="filter-blue-400" />,
      },
      {
        name: "Mistral",
        id: 4,
        panelTitle: "Mistral API Key",
        panelDescription: "Manage your Mistral API keys.",
        path: "mistral",
        outlineIcon: <ProviderWithLogo hideName provider="mistral" />,
        solidIcon: <ProviderWithLogo hideName provider="mistral" filter="filter-blue-400" />,
      },
      {
        name: "OpenRouter",
        id: 8,
        panelTitle: "OpenRouter API Key",
        panelDescription: "Manage your OpenRouter API keys.",
        path: "openrouter",
        outlineIcon: <ProviderWithLogo hideName provider="openrouter" />,
        solidIcon: <ProviderWithLogo hideName provider="openrouter" filter="filter-blue-400" />,
      },
      {
        name: "Amazon Bedrock",
        id: 5,
        panelTitle: "Amazon Bedrock Credentials",
        panelDescription: "Manage your Amazon Bedrock API keys.",
        path: "bedrock",
        outlineIcon: <ProviderWithLogo hideName provider="bedrock" />,
        solidIcon: <ProviderWithLogo hideName provider="bedrock" filter="filter-blue-400" />,
      },
      {
        name: "Azure OpenAI",
        id: 6,
        panelTitle: "Azure OpenAI Credentials",
        panelDescription: "Manage your Azure OpenAI API keys.",
        path: "openai-azure",
        outlineIcon: <ProviderWithLogo hideName provider="azure" />,
        solidIcon: <ProviderWithLogo hideName provider="azure" filter="filter-blue-400" />,
      },
      {
        name: "Vertex AI",
        id: 7,
        panelTitle: "Vertex AI Credentials",
        panelDescription: "Manage your Vertex AI API keys.",
        path: "vertex-ai",
        outlineIcon: <ProviderWithLogo hideName provider="vertexai" />,
        solidIcon: <ProviderWithLogo hideName provider="vertexai" filter="filter-blue-400" />,
      },
    ],
  },
  {
    name: "Environment Variables",
    panelTitle: "Workspace Environment Variables",
    group: "Workspace",
    panelDescription:
      "Auto-injected into every auto-executing tool in this workspace. Tool-level variables override these on conflict.",
    path: "env-vars",
    outlineIcon: <VariableIconOutline className="h-5 w-auto" />,
    solidIcon: <VariableIcon className="h-5 w-auto" />,
  },
  {
    name: "MCP Servers",
    panelTitle: "MCP Servers",
    group: "Workspace",
    panelDescription: "Save remote MCP connections and reuse them in prompts.",
    path: "mcp-servers",
    outlineIcon: <Mcp className="h-5 w-auto" />,
    solidIcon: <Mcp className="h-5 w-auto" />,
  },
  {
    name: "Slack",
    panelTitle: "Slack Integration",
    group: "Organization",
    panelDescription: "Connect your organization's Slack to chat with Wrangler AI.",
    path: "slack",
    outlineIcon: <SlackIcon className="h-5 w-auto" />,
    solidIcon: <SlackIcon className="h-5 w-auto" />,
  },
  {
    name: "Webhooks",
    panelTitle: "Your Webhooks",
    group: "Workspace",
    panelDescription: "Manage your webhook settings.",
    path: "webhook",
    docsLink: "https://docs.promptlayer.com/features/prompt-registry/webhooks#webhooks",
    outlineIcon: <SwitchVerticalIconOutline className="h-5 w-auto" />,
    solidIcon: <SwitchVerticalIcon className="h-5 w-auto" />,
  },
  {
    name: "Changelog",
    panelTitle: "Changelog",
    group: "Workspace",
    panelDescription: "Track runs, release label movements and updates across all entities.",
    path: "changelog",
    outlineIcon: <ClockIconOutline className="h-5 w-auto" />,
    solidIcon: <ClockIcon className="h-5 w-auto" />,
  },
  {
    name: "Configure Retention Policy",
    panelTitle: "Configure Retention Policy",
    group: "Workspace",
    panelDescription: "Choose how long request data is retained for this workspace.",
    path: "retention-policy",
    outlineIcon: <ArchiveIconOutline className="h-5 w-auto" />,
    solidIcon: <ArchiveIcon className="h-5 w-auto" />,
  },
].map((tab, index) => ({
  ...tab,
  id: index,
}));
